import type {
  ApiErrorBody,
  CreateSessionRequest,
  HealthResponse,
  SessionListResponse,
  SessionSummary,
} from '@teams-avatar/shared';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: ApiErrorBody,
  ) {
    super(body.message);
    this.name = 'ApiError';
  }

  /** Feldbezogene Fehler, damit das Formular sie am richtigen Eingabefeld zeigt. */
  get fields(): Record<string, string> {
    return this.body.fields ?? {};
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: {
      accept: 'application/json',
      ...(init.body === undefined ? {} : { 'content-type': 'application/json' }),
      ...(init.headers ?? {}),
    },
    credentials: 'same-origin',
  });

  if (!response.ok) {
    let body: ApiErrorBody = {
      error: 'http_error',
      message: `${response.status} ${response.statusText}`,
    };
    try {
      body = (await response.json()) as ApiErrorBody;
    } catch {
      // Antwort war kein JSON — der Fallback oben genuegt.
    }
    throw new ApiError(response.status, body);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export interface MeResponse {
  authenticated: boolean;
  mode: string;
  user?: { name: string; source: string; issuedAt: string };
}

export const api = {
  health: () => request<HealthResponse>('/api/health'),
  me: () => request<MeResponse>('/api/auth/me'),
  login: (password: string, name: string) =>
    request<{ user: { name: string } }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ password, name }),
    }),
  logout: () => request<void>('/api/auth/logout', { method: 'POST' }),
  sessions: () => request<SessionListResponse>('/api/sessions'),
  createSession: (input: CreateSessionRequest) =>
    request<SessionSummary>('/api/sessions', { method: 'POST', body: JSON.stringify(input) }),
  stopSession: (id: string) =>
    request<SessionSummary>(`/api/sessions/${id}/stop`, { method: 'POST' }),
  /**
   * Eine beendete Session erneut starten.
   *
   * Nur die Id: der volle Join-Link verlaesst das Backend nicht, der Server
   * holt ihn sich aus der Historie.
   */
  rejoinSession: (id: string) =>
    request<SessionSummary>(`/api/sessions/${id}/rejoin`, { method: 'POST' }),
  /** Einen Tastendruck an die AVA-Seite durchreichen. Erlaubt ist nur 'm'. */
  sendKey: (id: string, key: string) =>
    request<{ key: string }>(`/api/sessions/${id}/key`, {
      method: 'POST',
      body: JSON.stringify({ key }),
    }),
  /** Screenshots werden nie zwischengespeichert — der Zeitstempel erzwingt frische Daten. */
  screenshotUrl: (id: string) => `/api/sessions/${id}/screenshot?t=${Date.now()}`,
};
